import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  DirectionsCar as CarIcon,
  Assignment as TaskIcon,
  Checklist as ChecklistIcon,
  Schedule as ScheduleIcon,
  Chat as ChatIcon,
  NotificationsActive as NotificationsActiveIcon,
  Badge as BadgeIcon,
  ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import logo from '../assets/images/R-Allys-Logo-2104-(White).png';
import { sidebarItems } from '../constants/rbac';
import { useAdmin } from '../context/AdminContext';

const drawerWidth = 250;

const menuIcons = {
  dashboard: <DashboardIcon />,
  drivers: <PeopleIcon />,
  driverSchedule: <ScheduleIcon />,
  staffSchedule: <GroupIcon />,
  mySchedule: <ScheduleIcon />,
  garage: <CarIcon />,
  inspection: <ChecklistIcon />,
  tasks: <TaskIcon />,
  chatSupport: <ChatIcon />,
  pushNotifications: <NotificationsActiveIcon />,
  driverDetails: <BadgeIcon />,
  createUser: <ManageAccountsIcon />,
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess } = useAdmin();

  const isItemAllowed = (item) => hasAccess(item.module || item.key);

  const topLevelItems = sidebarItems.filter((item) => !item.parentKey);

  const getVisibleChildren = (parentKey) =>
    sidebarItems.filter(
      (item) => item.parentKey === parentKey && isItemAllowed(item)
    );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          bgcolor: '#2C2C2C',
          color: 'white'
        },
      }}
    >
      <Toolbar />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
          py: 1,
        }}
      >
        <img
          src={logo}
          alt="Rallys Logo"
          style={{
            maxWidth: '200px',
            height: '120px',
            objectFit:'fill',
          }}
        />
      </Box>
      
      <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.12)' }} />
      
      <List sx={{ mt: 0 }}>
        {topLevelItems.map((item) => {
          const visibleChildren = getVisibleChildren(item.key);
          const canAccessParent = isItemAllowed(item);
          const shouldShowParent = canAccessParent || visibleChildren.length > 0;

          if (!shouldShowParent) {
            return null;
          }

          const isParentSelected =
            location.pathname === item.path ||
            visibleChildren.some((child) => location.pathname === child.path);

          return (
            <Box key={item.path} sx={{ mb: 1 }}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={canAccessParent ? () => navigate(item.path) : undefined}
                  disabled={!canAccessParent}
                  selected={isParentSelected}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(239, 36, 36, 0.3)',
                      '&:hover': {
                        bgcolor: 'rgba(239, 36, 36, 0.3)',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mr: 0.5, color: isParentSelected ? '#DD2424' : 'rgba(255, 255, 255, 0.7)' }}>
                    {menuIcons[item.key]}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: isParentSelected ? 600 : 400,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {visibleChildren.map((child) => {
                const isChildSelected = location.pathname === child.path;

                return (
                  <ListItem key={child.path} disablePadding sx={{ mt: 0.5 }}>
                    <ListItemButton
                      onClick={() => navigate(child.path)}
                      selected={isChildSelected}
                      sx={{
                        mx: 1,
                        ml: 4,
                        borderRadius: 1,
                        minHeight: 36,
                        '&.Mui-selected': {
                          bgcolor: 'rgba(239, 36, 36, 0.22)',
                          '&:hover': {
                            bgcolor: 'rgba(239, 36, 36, 0.22)',
                          },
                        },
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 30, mr: 0.5, color: isChildSelected ? '#DD2424' : 'rgba(255, 255, 255, 0.65)' }}>
                        {menuIcons[child.key]}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.name}
                        sx={{
                          '& .MuiTypography-root': {
                            fontSize: '0.9rem',
                            fontWeight: isChildSelected ? 600 : 400,
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </Box>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
